import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { ErrorModalService } from '../../../core/services/error-modal.service';

@Component({
  selector: 'h2j-error-modal-host',
  imports: [],
  templateUrl: './error-modal.html',
  styleUrl: './error-modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ErrorModal {
  private readonly errorService = inject(ErrorModalService);

  readonly titleId = 'erro-modal-title';
  readonly detailId = 'erro-modal-detail';

  readonly currentError = this.errorService.currentError;
  readonly isOpen = computed(() => this.currentError() !== null);
  readonly title = computed(() => this.currentError()?.title ?? '');
  readonly detail = computed(() => this.currentError()?.detail ?? '');
  readonly traceId = computed(() => this.currentError()?.traceId ?? '');
  readonly fieldErrors = computed(() => this.currentError()?.fieldErrors ?? []);

  readonly copyState = signal<'idle' | 'copied'>('idle');

  private readonly closeBtn = viewChild<ElementRef<HTMLButtonElement>>('closeBtn');

  constructor() {
    effect(() => {
      if (this.isOpen()) {
        setTimeout(() => this.closeBtn()?.nativeElement.focus(), 0);
      }
    });
  }

  close(): void {
    const trigger = this.errorService.getTriggerElement();
    this.errorService.dismiss();
    this.copyState.set('idle');
    if (trigger) {
      setTimeout(() => trigger.focus(), 0);
    }
  }

  onOverlayKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  }

  async copyTrace(): Promise<void> {
    const id = this.traceId();
    if (!id) return;
    try {
      await navigator.clipboard?.writeText(id);
      this.copyState.set('copied');
      setTimeout(() => this.copyState.set('idle'), 2000);
    } catch {
      // Older browsers without clipboard API — silent no-op (user can still select the text manually)
    }
  }
}
